import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

/**
 *  TANTÁRGYFELOSZTÁS – a 20/2012. EMMI r. 105. § szerinti kötelező adatok a TTF felületén.
 *
 *  Külön osztályban, hogy a (nagy) TtfComponent csak példányosítsa: a sablon `jog.…`-on át
 *  éri el. Kiszolgáló: backend/ttf-jogszabaly.js (/api/ttf-jogszabaly).
 *
 *    - pedagógusonként: tantárgyfelosztási sorszám, végzettség, szakképzettség, lekötött
 *      óraszám (ha eltér a számítottól), órakedvezmény-jogcímek (a kinevezéseken felül),
 *      egyéb foglalkozások (amik a rácsban nem szerepelnek);
 *    - tanévenként: álláshelyek száma, nevelőtestületi vélemény, fenntartónak megküldve
 *      (105. § (3): augusztus 15-ig), jóváhagyva;
 *    - a 105. § szerinti nyomtatvány PDF-ben.
 */
export class TtfJogszabaly {
  adat: any = null;
  toltodik = false;
  hiba = '';
  tanev: any = {};
  /** A szerkesztett pedagógus másolata (null = nincs szerkesztés). */
  szerk: any = null;
  mentes = false;
  pdfToltodik = false;

  constructor(private http: HttpClient, private uzen: (szoveg: string, tipus?: string) => void) {}

  private ut(vege: string) { return `/api/ttf-jogszabaly/${vege}`; }

  async betolt(ev: number, intezmeny: string) {
    if (!ev || !intezmeny) return;
    this.toltodik = true;
    this.hiba = '';
    try {
      this.adat = await firstValueFrom(this.http.get<any>(this.ut(`${ev}/${intezmeny}`)));
      this.tanev = { ...(this.adat?.tanev || {}) };
    } catch (e: any) {
      this.adat = null;
      this.hiba = e?.error?.error || 'A 105. § szerinti adatok nem tölthetők be.';
    } finally {
      this.toltodik = false;
    }
  }

  /** A TTF többi nézete (Tanárok óraszáma) is ezt a lekötött óraszámot használja, ha betöltöttük. */
  lekotott(adid: string): number | null {
    const p = (this.adat?.pedagogusok || []).find((x: any) => x.munkatars === adid);
    return p ? Number(p.lekotott) : null;
  }

  figyelmeztetes(): any {
    const m = this.adat?.megkuldes;
    return m && (m.allapot === 'lejart' || m.allapot === 'kozeledik' || m.allapot === 'kesve_megkuldve') ? m : null;
  }
  megkuldesSzin(): string {
    return ({ megkuldve: 'success', kesve_megkuldve: 'warning', kozeledik: 'warning', lejart: 'danger', nyitott: 'medium' } as any)[this.adat?.megkuldes?.allapot] || 'medium';
  }

  async tanevMent(ev: number, intezmeny: string) {
    this.mentes = true;
    try {
      await firstValueFrom(this.http.put<any>(this.ut(`tanev/${ev}/${intezmeny}`), this.tanev));
      this.uzen('A tanév adatai mentve.');
      await this.betolt(ev, intezmeny);
    } catch (e: any) {
      this.uzen(e?.error?.error || 'A mentés nem sikerült.', 'error');
    } finally { this.mentes = false; }
  }

  async sorszamoz(ev: number, intezmeny: string) {
    try {
      const r = await firstValueFrom(this.http.post<any>(this.ut(`sorszamoz/${ev}/${intezmeny}`), {}));
      this.uzen(`${r.sorszamozva} pedagógus kapott sorszámot (névsorban, a meglévők után).`);
      await this.betolt(ev, intezmeny);
    } catch (e: any) {
      this.uzen(e?.error?.error || 'A sorszámozás nem sikerült.', 'error');
    }
  }

  szerkeszt(p: any) {
    this.szerk = {
      munkatars: p.munkatars, nev: p.nev, sorszam: p.sorszam, vegzettseg: p.vegzettseg || '',
      szakkepzettseg: p.szakkepzettseg || '', lekotott_oraszam: p.lekotott_oraszam,
      lekotott_szamitott: p.lekotott_szamitott, megjegyzes: p.megjegyzes || '',
      kinevezesek: p.kinevezesek || [],
      jogcimek: (p.jogcimek || []).map((j: any) => ({ ...j })),
      egyeb: (p.egyeb || []).map((e: any) => ({ ...e })),
    };
  }
  ujJogcim() { this.szerk.jogcimek.push({ jogcim: '', jogszabaly: '', ora: 0 }); }
  ujEgyeb() { this.szerk.egyeb.push({ megnevezes: '', osztaly_csoport: '', oraszam: 0 }); }

  async pedagogusMent(ev: number, intezmeny: string) {
    if (!this.szerk) return;
    this.mentes = true;
    try {
      await firstValueFrom(this.http.put<any>(this.ut(`pedagogus/${ev}/${intezmeny}`), this.szerk));
      this.uzen(`${this.szerk.nev} adatai mentve.`);
      this.szerk = null;
      await this.betolt(ev, intezmeny);
    } catch (e: any) {
      this.uzen(e?.error?.error || 'A mentés nem sikerült.', 'error');
    } finally { this.mentes = false; }
  }

  async pdf(ev: number, intezmeny: string) {
    this.pdfToltodik = true;
    try {
      const blob = await firstValueFrom(this.http.get(this.ut(`nyomtatvany/${ev}/${intezmeny}`), { responseType: 'blob' }));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tantargyfelosztas-${ev}-${ev + 1}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e: any) {
      this.uzen('A nyomtatvány nem készült el.', 'error');
    } finally { this.pdfToltodik = false; }
  }

  tetelSzoveg(p: any): string {
    return (p.tetelek || []).map((t: any) => `${t.tantargy} – ${t.osztaly}: ${t.ora}`).join('; ');
  }
  kedvezmenySzoveg(p: any): string {
    return [...(p.kinevezesek || []).map((k: any) => `${k.nev} (${k.ora})`),
      ...(p.jogcimek || []).map((j: any) => `${j.jogcim} (${j.ora})`)].join(', ') || '—';
  }
}
